import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { markExplored } from "@/lib/markExplored";
import { FollowableName } from "@/components/FollowableName";
import useIsMobile from "@/lib/useIsMobile";
import { useFilters } from "@/context/FiltersContext";
import FilterBar from "@/components/mobile/FilterBar";
import { money, fmtDate, canonEvent } from "@/lib/ui";
import { Loader2, Trophy, Medal, ChevronRight, MapPin } from "lucide-react";

const ASSOC_RED = "#C8000F";

export default function Results() {
  const m = useIsMobile();
  const nav = useNavigate();
  const { matches, assoc, events } = useFilters();
  const [rodeos, setRodeos] = useState(null);
  const [winners, setWinners] = useState(null);
  const [allRodeos, setAllRodeos] = useState(null);

  useEffect(() => {
    markExplored("results_audit");
    api.get("/pro/payouts").then((r) => setRodeos(r.data.rodeos || [])).catch(() => setRodeos([]));
    api.get("/results/recent?limit=60").then((r) => setWinners(r.data.winners || [])).catch(() => setWinners([]));
    api.get("/rodeos").then((r) => setAllRodeos(r.data || [])).catch(() => setAllRodeos([]));
  }, []);

  // id -> full rodeo record (has associations/country/status) so the shared filter can apply here too
  const rodeoById = useMemo(() => Object.fromEntries((allRodeos || []).map((r) => [r.id, r])), [allRodeos]);
  const passes = (rid) => { const r = rodeoById[rid]; return r ? matches(r) : assoc.size === 0; };

  const recent = [...(rodeos || [])].filter((r) => passes(r.rodeo_id)).sort((a, b) => (b.date || "").localeCompare(a.date || "")).slice(0, 30);
  // Winners are filtered by BOTH the rodeo-level filters AND each winner's own event,
  // so selecting e.g. Team Roping + Tie-Down shows only those event winners (not every
  // event at a rodeo that happens to offer them).
  const eventOkWinner = (w) => events.size === 0 || events.has(canonEvent(w.event));
  const winList = (winners || []).filter((w) => passes(w.rodeo_id) && eventOkWinner(w));

  const page = m ? "bg-[#0C1017] min-h-screen text-slate-200 pb-24" : "";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  const sub = m ? "text-slate-400" : "text-slate-500";
  const rowBg = m ? "bg-white/5 hover:bg-white/10" : "bg-[#F7F8FA] hover:bg-[#EEF0F2]";

  const loading = rodeos === null || winners === null || allRodeos === null;

  return (
    <div className={page} data-testid="results-page">
      <div className={`px-4 sm:px-6 py-5 border-b flex items-center gap-2 ${m ? "border-white/10" : "border-[#E4E7EB]"}`}>
        <Trophy className="w-6 h-6 text-[#C8000F]" />
        <div className="flex-1">
          <h1 className={`font-display text-xl font-bold ${title}`}>Results</h1>
          <div className={`text-xs ${sub}`}>Recently finished rodeos & who took the money</div>
        </div>
      </div>
      <div className={`px-4 sm:px-6 py-3 border-b ${m ? "border-white/10" : "border-[#E4E7EB]"}`}>
        <FilterBar variant={m ? "dark" : "light"} />
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>
      ) : (
        <div className="px-4 sm:px-6 py-5 max-w-5xl w-full mx-auto lg:mx-0 flex flex-col gap-6 lg:grid lg:grid-cols-2">
          {/* Recently finished rodeos */}
          <section data-testid="results-rodeos" className="min-w-0">
            <div className={`text-[11px] font-bold uppercase tracking-wider mb-2 ${sub}`}>Recently finished</div>
            {recent.length === 0 ? (
              <div className={`rounded-xl border border-dashed p-8 text-center text-sm ${m ? "border-white/15 text-slate-400" : "border-[#D7DBE0] text-slate-500"}`}>No finished rodeos with results yet.</div>
            ) : (
              <div className="space-y-2">
                {recent.map((r) => (
                  <button key={r.rodeo_id} onClick={() => nav(`/rodeo/${r.rodeo_id}`)} data-testid={`results-rodeo-${r.rodeo_id}`}
                    className={`w-full text-left rounded-xl border p-3.5 flex items-center gap-3 transition-colors ${card} ${m ? "active:bg-white/10" : "hover:border-slate-300"}`}>
                    <div className="min-w-0 flex-1">
                      <div className={`font-display font-bold text-[15px] leading-tight truncate ${title}`}><FollowableName name={r.name} /></div>
                      <div className={`text-xs mt-0.5 flex items-center gap-1.5 ${sub}`}>
                        <MapPin className="w-3 h-3" />{[r.city, r.state].filter(Boolean).join(", ") || "—"}{r.date ? ` · ${fmtDate(r.date)}` : ""}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-display font-bold text-[#3B8A2B]">{money(r.total_paid)}</div>
                      <div className={`text-[10px] uppercase tracking-wide ${sub}`}>paid out</div>
                    </div>
                    <ChevronRight className={`w-4 h-4 shrink-0 ${sub}`} />
                  </button>
                ))}
              </div>
            )}
          </section>

          {/* Winners feed */}
          <section data-testid="results-winners" className="min-w-0">
            <div className={`text-[11px] font-bold uppercase tracking-wider mb-2 ${sub}`}>Latest event winners</div>
            {winList.length === 0 ? (
              <div className={`rounded-xl border border-dashed p-8 text-center text-sm ${m ? "border-white/15 text-slate-400" : "border-[#D7DBE0] text-slate-500"}`}>No winners posted yet.</div>
            ) : (
              <div className="space-y-2">
                {winList.map((w, i) => (
                  <button key={`${w.rodeo_id}-${w.event}-${i}`} onClick={() => w.rodeo_id && nav(`/rodeo/${w.rodeo_id}`)} data-testid={`results-winner-${i}`}
                    className={`w-full text-left rounded-lg p-3 flex items-center gap-3 transition-colors ${rowBg}`}>
                    <span className="w-8 h-8 rounded-full bg-[#E68A2E]/15 text-[#E68A2E] flex items-center justify-center shrink-0"><Medal className="w-4 h-4" /></span>
                    <div className="min-w-0 flex-1">
                      <div className={`font-semibold text-sm truncate ${title}`}><FollowableName name={w.rider} /></div>
                      <div className={`text-xs truncate ${sub}`}>{w.event} · {w.rodeo_name}{w.stock ? ` · ${w.stock}` : ""}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-display font-bold text-sm text-[#3B8A2B]">{money(w.earnings)}</div>
                      {w.value != null && w.value !== "" && <div className={`text-[10px] ${sub}`}>{w.value}</div>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
